import { FC, useMemo } from "react";
import { SubscriptionFormSignStore } from "./SubscriptionFormSignStore";
import { BottomSpacer, Box, Card, Grid, GridItem, VBox } from "@/components-v2/Box";
import { formatOrgNum, isBase64 } from "@/utils/other";
import { Button, LinkButton } from "@/components-v2/Button";
import { formatDate } from "@/i18n/dateFormat";
import { HiddenForMobile, VisibleOnMobile } from "@/components-v2/Mobile";
import { isMobileView } from "@/utils/screen";
import { Alert } from "@/components-v2/Alert";
import { observer } from "mobx-react";
import { SigningModel } from "@/views/Pages/common/signingDomain";
import { SubscriptionFormDialogState, SubscriptionModel } from "./subscriptionFormDomain";
import { pdfjs } from "react-pdf";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import parse from "html-react-parser";
import { SigningComponent } from "@/views/Pages/common/signingComponents";
import { CURRENCY_NOK, formatAmount } from "@/utils/currency";
import { MTextInput } from "@/components-v2/TextInput";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { Icon } from "@/components-v2/Icon";
import { ViewDocumentSigningStatusDialog } from "@/dbot/summary/summaryComponents";
import { PdfPreview } from "@/components-v2/PdfPreview";
import { dcompanyFrontpageUrl } from "@/utils/navigation";
import { DcompanyLogoImg } from "@/components-v2/Logo";
import {
  useLanguage,
  useLocaleFormat,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { InfoButton } from "@/components-v2/InfoButton";
import { findShareTypeName, shareTypeLabel } from "@/common/share";
import { QueryParams, singleValue } from "@/utils/queryParams";
import { Language } from "@/i18n/languages";
import { pdfjs_version } from "@/components-v2/pdfjs.version";
import { Markdown } from "@/text/Markdown.tsx";

pdfjs.GlobalWorkerOptions.workerSrc = pdfjs_version;

const dCompanyLogo = (lang: Language) => {
  return (
    <a href={dcompanyFrontpageUrl(lang)}>
      <DcompanyLogoImg full style={{ maxWidth: 200 }} />
    </a>
  );
};

export const StickyDCompanyHeader: FC<{ companyName?: string }> = observer(({ companyName }) => {
  const { t, lang } = useTranslation();
  return (
    <>
      <VisibleOnMobile>
        <Box justifyContent={"center"} borderBottom bg padding>
          <div>{dCompanyLogo(lang)}</div>
        </Box>
      </VisibleOnMobile>
      <HiddenForMobile>
        <Box justifyContent={"space-between"} borderBottom bg padding>
          <Box alignItems={"center"}>
            {dCompanyLogo(lang)}
            <div>{companyName ? companyName : t("signing.slogan")}</div>
          </Box>
          <Box alignItems={"center"} marginRight={"lg"} gap={"lg"}>
            <Markdown value={t("signing.externalLink1")} enableLinks />
            <Markdown value={t("signing.externalLink2")} enableLinks />
          </Box>
        </Box>
      </HiddenForMobile>
    </>
  );
});

const SubscriptionFormSign: FC<{ queryParams: QueryParams }> = ({ queryParams }) => {
  const invalidRef = "invalid-ref" in queryParams;
  return (
    <>
      {invalidRef ? (
        <>
          <InvalidRef />
        </>
      ) : (
        <>
          <SubscriptionFormSignPage queryParams={queryParams} />
        </>
      )}
    </>
  );
};

function InvalidRef() {
  const { t } = useTranslation();
  return (
    <>
      <StickyDCompanyHeader />
      <Box alignItems={"center"} justifyContent={"center"} vertical padding>
        <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
        <h1>{t("signing.invalidRefTitle")}</h1>
        <div>{t("signing.invalidRefText")}</div>
      </Box>
    </>
  );
}

const SubscriptionFormSignPage: FC<{ queryParams: QueryParams }> = observer(({ queryParams }) => {
  const { apiService, http } = useRootStore();
  const messageStore = useMessageStore();
  const documentReference = singleValue(queryParams.ref) ?? null;
  const store = useMemo(() => {
    const documentSignStore = new SubscriptionFormSignStore(
      messageStore,
      apiService,
      http,
      documentReference,
    );
    documentSignStore.fetchDocumentDetails();
    return documentSignStore;
  }, [messageStore, apiService, http, documentReference]);
  const signingModel = useMemo(
    () => new SigningModel(apiService, documentReference),
    [documentReference, apiService],
  );

  return (
    <>
      <FullScreenLayout
        appBar={<StickyDCompanyHeader companyName={store.document?.companyName} />}
        centerPanel={<SubscriptionFormSignBody store={store} signingModel={signingModel} />}
        alertStore={messageStore}
      />
      <SubscriptionFormDialog state={store.dialogState} />
    </>
  );
});

const SubscriptionFormSignBody: FC<{
  store: SubscriptionFormSignStore;
  signingModel: SigningModel;
}> = observer(({ store, signingModel }) => {
  const { t } = useTranslation();

  switch (store.state) {
    case "loading":
      return <LoadingIndicator fullscreen />;
    case "fetchedError":
      return (
        <Box alignItems={"center"} justifyContent={"center"} vertical padding>
          <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
          <div>{t("signing.fetchedError")}</div>
        </Box>
      );
    case "subscriptionSubmitError":
      return (
        <Box alignItems={"center"} justifyContent={"center"} vertical padding>
          <Icon name={"warning-circle"} size={"jumbo"} color={"warning"} />
          <div>{t("signing.subscriptionSubmitError")}</div>
        </Box>
      );
    case "documentSigned":
      return (
        <Box alignItems={"center"} justifyContent={"center"} vertical padding>
          <Icon name={"check-circle"} size={"jumbo"} color={"success"} />
          <h4>{t("signing.documentSigned")}</h4>
          <div>{t("signing.documentSigned.description")}</div>
        </Box>
      );
    case "documentFetched":
    case "documentRead": {
      return (
        <>
          {store.document?.documentContentBase64 && (
            <Box alignItems={"center"} justifyContent={"center"} paddingTop={"md"}>
              {isBase64(store.document?.documentContentBase64) ? (
                <PdfPreview
                  file={{
                    url: `data:application/pdf;base64,${store.document?.documentContentBase64}`,
                  }}
                />
              ) : (
                parse(store.document?.documentContentBase64, { trim: true })
              )}
            </Box>
          )}
          <BottomSpacer />
          <BottomSpacer />
          <div className={"fixed bottom-0 z-1 bg-default border-t border-gray w-full z-10"}>
            <Box
              justifyContent={"center"}
              alignItems={"center"}
              padding
              gap={isMobileView() ? "sm" : "lg"}
              vertical={isMobileView()}
            >
              <Button
                variant={"secondary"}
                label={t("signing.subscription.download")}
                loading={store.isDownloadingDocument}
                icon={"file-pdf"}
                onClick={store.download}
              />
              {store.subscription && (
                <>
                  <Box>{t("or")}</Box>
                  {store.subscription.isShareIssue ? (
                    <Button
                      variant={"primary"}
                      label={t("signing.subscription.open")}
                      onClick={store.setElectronicSubscriptionForm}
                    />
                  ) : (
                    <Button
                      variant={"primary"}
                      label={t("signing.sign")}
                      onClick={() =>
                        store
                          .submitSubscriptionFormDetails()
                          .then(() => signingModel.signRequest(store.document?.signingMethod))
                      }
                    />
                  )}
                </>
              )}
            </Box>
          </div>
        </>
      );
    }
    case "electronicSubscriptionForm":
      return <ElectronicSubscriptionFormBody store={store} signingModel={signingModel} />;
    case "signingInProgress": {
      return (
        <SigningComponent
          signingModel={signingModel}
          signingMethod={store.document?.signingMethod}
          signerName={store.document?.signerName ?? ""}
        />
      );
    }
    default:
      return <LoadingIndicator fullscreen />;
  }
});

export const ElectronicSubscriptionFormBody: FC<{
  store: SubscriptionFormSignStore;
  signingModel: SigningModel;
}> = observer(({ store, signingModel }) => {
  const { t } = useTranslation();
  return (
    <>
      <VBox gap={"xl"} paddingTop={"md"} maxWidth={"md"} container>
        {store.subscription?.isMultipleSigners && (
          <Alert
            type="info"
            message={
              <div>
                {t("signing.subscription.signingStatus", {
                  signed: store.subscription?.numberOfSignersWhoHasSigned,
                  signers: store.subscription?.numberOfSigners,
                  subscriberName: store.subscription?.subscriber.name,
                })}{" "}
                <LinkButton
                  label={t("signing.subscription.signingStatus.show")}
                  onClick={store.openSigningStatusDialog}
                />
              </div>
            }
          />
        )}
        <ElectronicSubscriptionForm
          companyName={store.document?.companyName}
          organisationNumber={store.document?.organisationNumber}
          subscription={store.subscription}
        />
      </VBox>
      <ElectronicSubscriptionFormButtons
        onBack={store.setDocumentRead}
        onSubmit={() =>
          store
            .submitSubscriptionFormDetails()
            .then(() => signingModel.signRequest(store.document?.signingMethod))
        }
        isSubmitting={store.isSubmittingSubscriptionForm}
        isSubmitDisabled={
          !store.subscription?.isValid || !store.subscription?.hasSubscriptionPeriodStarted
        }
      />
    </>
  );
});

export const ElectronicSubscriptionForm: FC<{
  companyName?: string;
  organisationNumber?: string;
  subscription: SubscriptionModel | null;
}> = observer(({ companyName, organisationNumber, subscription }) => {
  return (
    <Card padding={isMobileView() ? "md" : "lg"} gap={"xl"}>
      <SubscriptionHeader
        companyName={companyName}
        organisationNumber={organisationNumber}
        subscription={subscription}
      />
      <SubscriptionForm subscription={subscription} />
    </Card>
  );
});

export function SubscriptionHeader({
  companyName,
  organisationNumber,
  subscription,
}: {
  companyName?: string;
  organisationNumber?: string;
  subscription: SubscriptionModel | null;
}) {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormat();
  return (
    <>
      <VisibleOnMobile>
        <VBox gap={false}>
          <Box alignItems={"center"}>
            <Icon name={"buildings"} />
            <div className={"text-sm"}>{companyName}</div>
          </Box>
          <Box alignItems={"center"}>
            <Icon name={"user-circle"} />
            <div className={"text-sm"}>{subscription?.subscriber.name}</div>
          </Box>
        </VBox>
      </VisibleOnMobile>
      <HiddenForMobile>
        <Box padding={"lg"} alignItems={"start"} justifyContent={"space-between"}>
          <VBox gap={false}>
            <div>{t("signing.subscription.capitalIncrease")}</div>
            <strong>{companyName}</strong>
            <div>
              {t("org-no-with-value", {
                orgNo: formatOrgNum(organisationNumber),
              })}
            </div>
          </VBox>
          <VBox gap={false} alignItems={"end"}>
            <div>{t("signing.subscription.subscriber")}</div>
            <strong>{subscription?.subscriber.name}</strong>
            {subscription?.subscriberBirthdateOrOrgNo && (
              <div>
                {subscription?.isCompanySubscriber
                  ? t("org-no-with-value", {
                      orgNo: formatOrgNum(subscription.subscriberBirthdateOrOrgNo),
                    })
                  : t("signing.subscription.birthdate", {
                      birthdate: formatDate(subscription?.subscriberBirthdateOrOrgNo),
                    })}
              </div>
            )}
            {subscription?.isMultipleSigners && (
              <div>
                {t("signing.subscription.representative", {
                  name: subscription.signerName,
                })}
              </div>
            )}
          </VBox>
        </Box>
      </HiddenForMobile>
    </>
  );
}

export const SubscriptionForm: FC<{
  subscription: SubscriptionModel | null;
}> = observer(({ subscription }) => {
  const { t, lang } = useTranslation();
  const locale = useLanguage();
  const { formatNumber } = useLocaleFormat();

  if (subscription === null)
    return <Alert type="error" message={t("signing.subscription.form.error")} />;

  return (
    <VBox gap={isMobileView() ? "md" : "lg"} padding={isMobileView() ? false : "lg"}>
      <h3>{t("signing.subscription.form.title")}</h3>
      {subscription?.isSubscriptionPeriod && (
        <span>
          {t("signing.subscription.form.subscriptionPeriod", {
            subscriptionPeriodTo: formatDate(locale, subscription.subscriptionPeriod.to),
          })}{" "}
          {subscription.isPreferentialRights && (
            <>
              {t(subscription?.overSubscriptionLabel)}
              <InfoButton infoText={t("signing.subscription.form.overSubscription.infoText")} />
            </>
          )}
        </span>
      )}
      {subscription?.showSubscriptionPeriodEnded && (
        <Box>
          {t("signing.subscription.form.subscriptionPeriod.hasEnded", {
            subscriptionEnd: formatDate(locale, subscription?.subscriptionPeriod.to),
          })}
        </Box>
      )}
      {subscription?.showSubscriptionPeriodNotStarted && (
        <Box>
          {t("signing.subscription.form.subscriptionPeriod.notStarted", {
            subscriptionStart: formatDate(locale, subscription?.subscriptionPeriod.from),
          })}
        </Box>
      )}
      {subscription.isSubscriptionSigned && (
        <Box>
          <span>
            <strong>{t("signing.subscription.form.pleaseNote")}</strong>
            {t("signing.subscription.form.isSignedBy", { name: subscription.firstSigner?.name })}
          </span>
        </Box>
      )}
      {subscription?.shares.map((share) => {
        return (
          <VBox
            bg={"alt2"}
            key={share.shareTypeId}
            padding={isMobileView() ? "md" : "lg"}
            gap={isMobileView() ? "md" : "lg"}
          >
            <Grid>
              <GridItem xs={12} md={8}>
                <VBox gap={"md"}>
                  <strong>
                    {share.shareType
                      ? findShareTypeName(share.shareType, lang)
                      : t(shareTypeLabel(share.shareTypeCode))}
                  </strong>
                  <VBox gap={false}>
                    <div>
                      {t("signing.subscription.form.sharePrice", {
                        currency: CURRENCY_NOK,
                        sharePrice: formatNumber(share.sharePrice),
                      })}
                    </div>
                    <div>
                      {t("signing.subscription.form.shares.available", {
                        availableShares: formatNumber(share.availableShares),
                      })}
                    </div>
                  </VBox>
                </VBox>
              </GridItem>
              <GridItem xs={12} md={4}>
                <MTextInput
                  model={share.numberOfShares}
                  label={t("signing.subscription.form.numberOfShares")}
                  disabled={subscription.isDisabled}
                />
              </GridItem>
            </Grid>
            {subscription.isMultipleShareTypes && (
              <>
                <VisibleOnMobile>
                  <VBox gap={false}>
                    <Box>{t("signing.subscription.form.subscriptionPrice")}</Box>
                    <strong>{formatAmount(locale, CURRENCY_NOK, share.subscriptionPrice)}</strong>
                  </VBox>
                </VisibleOnMobile>
                <HiddenForMobile>
                  <Box alignItems={"start"} justifyContent={"space-between"}>
                    <Box>{t("signing.subscription.form.subscriptionPrice")}</Box>
                    <strong>{formatAmount(locale, CURRENCY_NOK, share.subscriptionPrice)}</strong>
                  </Box>
                </HiddenForMobile>
              </>
            )}
          </VBox>
        );
      })}
      <Box
        alignItems={"start"}
        justifyContent={"space-between"}
        paddingX={isMobileView() ? "md" : "lg"}
        paddingBottom={"md"}
      >
        <strong>{t("signing.subscription.form.totalSubscriptionPrice")}</strong>
        <strong>{formatAmount(locale, CURRENCY_NOK, subscription?.totalSubscriptionPrice)}</strong>
      </Box>
    </VBox>
  );
});

export const ElectronicSubscriptionFormButtons: FC<{
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
}> = observer(({ onBack, onSubmit, isSubmitting, isSubmitDisabled }) => {
  const { t } = useTranslation();
  return (
    <div className={"fixed bottom-0 z-1 bg-default border-t border-gray w-full z-10"}>
      <Box justifyContent={"center"}>
        <Box alignItems={"center"} padding maxWidth={"md"} container>
          <Button variant={"secondary"} arrow={"left"} label={t("back")} onClick={onBack} />
          <Button
            variant={"primary"}
            fullWidth
            label={t("signing.sign")}
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitDisabled}
          />
        </Box>
      </Box>
    </div>
  );
});

export const SubscriptionFormDialog = observer(function ({
  state,
}: {
  state: SubscriptionFormDialogState;
}) {
  switch (state.type) {
    case "viewSigningStatus":
      return <ViewDocumentSigningStatusDialog model={state.model} />;
    default:
      return null;
  }
});

export default SubscriptionFormSign;
